/**
 * R3 (spec §5): the unified "AI Updates" toolbar control. Replaces the old header `+N proposed`
 * badge AND the `✨ N AI proposals` chip with one control covering BOTH record-level (D8, "New")
 * and field-level (M3.4, "Updated") proposals.
 *
 * Primary button toggles the AI-Updates filter off ↔ its last scope; the ▾ caret picks an explicit
 * scope (New records / Updated rows / Both). Counts are row-distinct and view-scoped; when the
 * current view hides some, a "· N hidden" suffix appears (§5.5). The parent renders this only when
 * proposals exist or a saved zero-result AI filter is active, so saved query intent stays clearable.
 */
import { useRef, useState } from 'react';

import styles from './crm.module.css';
import { CrmPopover } from './CrmPopover';
import type { ProposalsFilter } from './crmViewConfig';

interface Props {
  filter: ProposalsFilter;
  /** row-distinct counts scoped to the current view (what's visible) */
  newCount: number;
  updatedCount: number;
  /** proposal rows filtered out by the current view */
  hiddenCount: number;
  /** whether each kind exists at all (from backend counts, not row-capped) */
  newAvailable: boolean;
  updatedAvailable: boolean;
  /** the loaded set hit FETCH_CAP → counts are a floor, not list-wide truth */
  capped: boolean;
  onToggle: () => void;
  onSetScope: (f: ProposalsFilter) => void;
}

export function CrmAiUpdatesControl({
  filter, newCount, updatedCount, hiddenCount, newAvailable, updatedAvailable, capped,
  onToggle, onSetScope,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const caretRef = useRef<HTMLButtonElement>(null);

  const parts: string[] = [];
  if (newCount > 0) parts.push(`${newCount} New`);
  if (updatedCount > 0) parts.push(`${updatedCount} Updated`);
  const body = parts.length ? parts.join(' & ') : '0';
  const active = filter !== 'off';
  // the ▾ scope menu is only meaningful when BOTH kinds exist; with one kind the primary toggle is
  // the whole control (its 'all' scope resolves to that one kind).
  const showCaret = newAvailable && updatedAvailable;

  const scopeLabel: Record<Exclude<ProposalsFilter, 'off'>, string> = {
    new: 'New records', updated: 'Updated rows', all: 'Both',
  };
  const pick = (f: ProposalsFilter) => { onSetScope(f); setMenuAnchor(null); };

  return (
    <span className={styles.aiUpdatesWrap}>
      <button
        className={`${styles.aiUpdatesBtn} ${active ? styles.aiUpdatesActive : ''}`}
        aria-pressed={active}
        title={active
          ? 'Showing rows with AI updates — click to clear'
          : 'Filter to rows with AI updates'}
        onClick={onToggle}
      >
        ✨ AI Updates: {body}{capped ? '+' : ''}
        {active ? ' ✓' : ''}
        {hiddenCount > 0 && (
          <span
            className={styles.aiUpdatesHidden}
            title={`${hiddenCount} additional AI proposal${hiddenCount === 1 ? '' : 's'} hidden by current filters — clear filters to see ${hiddenCount === 1 ? 'it' : 'them'}.`}
          > · {hiddenCount} hidden</span>
        )}
      </button>
      {showCaret && (
        <button
          ref={caretRef}
          className={`${styles.aiUpdatesCaret} ${active ? styles.aiUpdatesActive : ''}`}
          aria-label="Choose which AI updates to show"
          aria-haspopup="menu"
          aria-expanded={menuAnchor != null}
          onClick={() => setMenuAnchor((a) => (a ? null : caretRef.current))}
        >▾</button>
      )}
      {menuAnchor && (
        <CrmPopover anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} align="left">
          {(['new', 'updated', 'all'] as const).map((f) => (
            <button
              key={f}
              role="menuitemradio"
              aria-checked={filter === f}
              className={styles.popoverItem}
              onClick={() => pick(f)}
            >
              <span aria-hidden="true">{filter === f ? '● ' : '○ '}</span>{scopeLabel[f]}
            </button>
          ))}
        </CrmPopover>
      )}
    </span>
  );
}
